import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { EnumViewModel, HalfDayType, HolidayDto, LeaveType, NotificationAction, SystemNotificationDto, UserDto } from 'app/api/models';
import { DealService, EmployeeService, HolidayService, SystemNotificationService } from 'app/api/services';
import { AppAuthService } from 'app/api/services/cookie-jwt-auth/app-auth.service';
import { sortOnProperty } from 'app/app.component';
import { AddItemDialogComponent } from 'app/core/components/add-item-dialog/add-item-dialog.component';
import { TimeUtil } from 'app/core/utils/time-utils';
import { QuoteWizardDialogComponent } from 'app/views/admin/crm/pipeline/pipeline-board/quote-wizard-dialog/quote-wizard-dialog.component';
import { HolidayUtilities } from 'app/views/admin/erp/hr/holiday-utilities/holiday-utilities';
import { ScrumboardCardDetailsComponent } from 'app/views/admin/pages/projects/project/scrumboard/card/details/details.component';
import { PurchaseOrderBuilderComponent } from 'app/views/admin/pages/purchase-orders/purchase-order-builder/purchase-order-builder.component';
import { isNumber } from 'lodash';
import { Subject } from 'rxjs';

@Component({
    selector       : 'notifications',
    templateUrl    : './notifications.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'notifications',
    standalone     : true,
    imports        : [MatButtonModule, NgIf, MatIconModule, MatTooltipModule, NgFor, NgClass, NgTemplateOutlet, RouterLink, DatePipe],
})
export class NotificationsComponent implements OnInit, OnDestroy
{
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    notifications: SystemNotificationDto[] = [];
    unreadCount: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    allUsers: UserDto[] = [];
    public leaveTypes = Object.values(LeaveType).filter(x => isNumber(x)).map(x => <any> {name: HolidayUtilities.getLeaveTypeName(+x), value: <LeaveType>x});

    public NotificationAction = NotificationAction;

    /**
     * Constructor
     */
    constructor(
        private notifService: SystemNotificationService,
        private holidayService: HolidayService,
        private employeeService: EmployeeService,
        private dealService: DealService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private appAuth: AppAuthService,
        private dialog: MatDialog,
        private _fuseConfirmationService: FuseConfirmationService,
        private router: Router,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this.loadNotifications();
        this.loadUsers();
    }

    loadNotifications() {
        this.notifService.apiSystemNotificationForUserGet$Json$Response({userId: this.appAuth.getUser().id}).subscribe(res => {
            if(!res?.body?.data) return;

            this.notifications = res.body.data;
            // console.log("notifications", this.notifications);

            this._calculateUnreadCount();

            this._changeDetectorRef.markForCheck();
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if ( this._overlayRef )
        {
            this._overlayRef.dispose();
        }
    }

    loadUsers() {
        this.employeeService.apiEmployeeSearchEmployeeUsersGet$Json$Response().subscribe(res => {
            if(!res?.body.data) return;

            this.allUsers = res.body.data;
        });
    }

    isNotificationType = (notification: SystemNotificationDto, action: NotificationAction) => notification.action == action;

    openPurchaseOrder(purchaseOrderId: number, notification: SystemNotificationDto) {
        this.dialog.open(PurchaseOrderBuilderComponent, {
            data: {
                purchaseOrderId: purchaseOrderId,
            },
            autoFocus: false,
        }).afterClosed().subscribe(() => {
            if(!notification.read)
                this.toggleRead(notification);
        });
    }

    openDeal(dealId: number, notification: SystemNotificationDto) {
        this.dealService.apiDealDealGet$Json$Response({dealId: dealId}).subscribe(res => {
            if(!res?.body?.data) return;
            
            this.dialog.open(QuoteWizardDialogComponent, {disableClose: true, data: {deal: res.body.data}}).afterClosed().subscribe(() => {
                if(!notification.read)
                    this.toggleRead(notification);
            });
        })
    }

    openAsset(assetId: number, notification: SystemNotificationDto) {
        console.log("opening asset", assetId);
        this.dialog.open(ScrumboardCardDetailsComponent, {autoFocus: false, data: { cardId: assetId, isAssetBoard: true }}).afterClosed().subscribe(() => {
            if(!notification.read)
                this.toggleRead(notification);
        });
    }

    goToSupplier(supplierId: number, notification: SystemNotificationDto) {
        if(!notification.read)
            this.toggleRead(notification);

        this.router.navigateByUrl('/suppliers/supplier-details/' + supplierId);
    }
    
    openHoliday(holidayId: number, notification: SystemNotificationDto) {
        this.holidayService.apiHolidayHolidayGet$Json$Response({holidayId: holidayId}).subscribe(res => {
            if(!res?.body?.data) return;

            this.openHolidayApproval(res.body.data);

            if(!notification.read)
                this.toggleRead(notification);
        });
    }

    private openHolidayApproval(currentValue: HolidayDto) {
        var users = sortOnProperty(this.allUsers.map(x => <EnumViewModel>{name: this.GetUserName(x.id), value: x.id}), 'name');
        var halfDayOptions = ['Full Day', 'AM', 'PM'].map((x, i) => <EnumViewModel>{name: x, value: i});
    
        var dialog = this.dialog.open(AddItemDialogComponent, {
            data: {
                title: `${currentValue ? 'Edit' : 'Add'} Leave`,
                items: [
                    {name: 'user', propertyName: 'userId', type: 'select', options: users, initialValue: currentValue?.userId ?? null, colSpan: 9, mandatory: true},
                    {name: 'private description', propertyName: 'isPrivate', type: 'toggle', initialValue: currentValue?.isPrivate ?? false, colSpan: 3},
                    {name: 'leave type', propertyName: 'leaveType', type: 'select', options: this.leaveTypes, initialValue: currentValue?.leaveType ?? null, colSpan: 12, mandatory: true},
    
                    {name: 'from', type: 'date', initialValue: currentValue?.from, colSpan: 3, mandatory: true},
                    {name: 'to', type: 'date', initialValue: currentValue?.to, colSpan: 3, mandatory: true},
                    {name: 'time of day', propertyName: 'halfDayType', type: 'select', options: halfDayOptions, initialValue: currentValue?.halfDayType ?? 0, colSpan: 3},
                    {name: 'hours', initialValue: currentValue?.hours ?? 3.5, colSpan: 3, hideConfig: {property: 'halfDayType', value: 0}},
    
                    {name: 'description', propertyName: 'name', initialValue: currentValue?.name ?? '', colSpan: 12},
                ],
                overrideWidth: 50,
                showDeleteButton: true,
                overrideDeleteText: currentValue?.requiresApproval ? 'Reject' : 'Delete',
                buttons: currentValue?.requiresApproval ? [
                    {name: 'Approve', closeDialog: true, callback: () => this.ApproveHoliday(currentValue)},
                ] : null,
            }
        });
        
        dialog.afterClosed().subscribe((dialogRes) => {
            if (!dialogRes) return;
    
            if(dialogRes.delete) {
              this.archiveTask(currentValue);
              return;
            }
    
            var holiday: HolidayDto = {
                name: dialogRes.name,
                leaveType: +dialogRes.leaveType,
                userId: dialogRes.userId,
                from: TimeUtil.setTo1amUTC(new Date(dialogRes.from)).toISOString(),
                to: TimeUtil.setTo1amUTC(new Date(dialogRes.to)).toISOString(),
                days: TimeUtil.getDaysBetween(new Date(dialogRes.from), new Date(dialogRes.to)) + 1,
                isPrivate: dialogRes.isPrivate,
                halfDayType: dialogRes.halfDayType,
                hours: dialogRes.halfDayType == HalfDayType.FullDay ? 7 : (dialogRes.hours || 3.5),
                requiresApproval: currentValue.requiresApproval,
    
                holidayId: currentValue?.holidayId ?? null,
            };

            this.saveUpdatedTask(holiday);
        });
    }

    private ApproveHoliday(holiday: HolidayDto) {
        holiday.requiresApproval = false;
        this.saveUpdatedTask(holiday);
    }

    private saveUpdatedTask(holiday: HolidayDto) {
        this.holidayService.apiHolidayUpdateHolidayPut$Json$Response({body: holiday}).subscribe(res => {
            if(!res.body.data) return;
        });
    }

    public archiveTask(holiday: HolidayDto, confirm: boolean = false) {
        if(!confirm) {
            this.archiveTaskApi(holiday);
            return;
        }

        const confirmation = this._fuseConfirmationService.open({
            title  : 'Delete holiday',
            message: `Are you sure you want to remove this holiday?`,
            actions: {
                confirm: {
                    label: 'Delete',
                },
            },
        });

        confirmation.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                this.archiveTaskApi(holiday);
            }
        });
    }

    private archiveTaskApi(holiday: HolidayDto) {
        this.holidayService.apiHolidayArchiveHolidayDelete$Json$Response({holidayId: holiday.holidayId}).subscribe(res => {
            if(!res?.body?.data) return;
        });
    }
    
    GetUserName(userId: number) {
        if(!this.allUsers) return '';

        for(var u of this.allUsers)
            if(u.id == userId) return `${u.firstName} ${u.lastName}`;

        return '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void
    {
        // Return if the notifications panel or its origin is not defined
        if ( !this._notificationsPanel || !this._notificationsOrigin )
        {
            return;
        }

        // Create the overlay if it doesn't exist
        if ( !this._overlayRef )
        {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void
    {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void
    {
        for(var notif of this.notifications)
            notif.read = true;

        this.notifService.apiSystemNotificationMarkAllReadPut$Json$Response({userId: this.appAuth.getUser().id}).subscribe();
        
        this._calculateUnreadCount();
    }

    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: SystemNotificationDto): void
    {
        notification.read = !notification.read;

        this.notifService.apiSystemNotificationUpdateNotificationPut$Json$Response({body: notification}).subscribe();

        this._calculateUnreadCount();
    }

    /**
     * Delete the given notification
     */
    dismiss(notification: SystemNotificationDto): void
    {
        notification.dismissed = true;

        this.notifService.apiSystemNotificationUpdateNotificationPut$Json$Response({body: notification}).subscribe(res => {
            if(!res?.body?.data) return;

            this.notifications = this.notifications.filter(x => x.systemNotificationId != notification.systemNotificationId);
            this._calculateUnreadCount();
        });

        this._calculateUnreadCount();
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void
    {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop     : true,
            backdropClass   : 'fuse-backdrop-on-mobile',
            scrollStrategy  : this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX : 'start',
                        originY : 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX : 'start',
                        originY : 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX : 'end',
                        originY : 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX : 'end',
                        originY : 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() =>
        {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void
    {
        let count = 0;

        if ( this.notifications && this.notifications.length )
        {
            count = this.notifications.filter(notification => !notification.read).length;
        }

        this.unreadCount = count;

        this._changeDetectorRef.markForCheck();
    }
}
